import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QrCode, Star, Ticket } from "lucide-react";
import api from "@/lib/api";
import { PageHero, Reveal, useSettings, siteText } from "@/components/shared";

export default function Fidelite() {
  const settings = useSettings();
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get("/programme").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const rate = cfg?.loyalty_points_per_euro ?? 1;
  const threshold = cfg?.loyalty_reward_threshold ?? 200;
  const value = cfg?.loyalty_reward_value ?? 10;

  const STEPS = [
    { n: "01", icon: Star, title: "Cumulez automatiquement", text: `${rate} € dépensé = ${rate} point. Vos points sont crédités après chaque commande ou prestation payée en ligne, ou validés sur place par notre équipe.` },
    { n: "02", icon: QrCode, title: "Présentez votre carte", text: "Votre carte de fidélité numérique avec QR code vous attend dans votre espace client — présentez-la simplement depuis votre téléphone." },
    { n: "03", icon: Ticket, title: "Profitez de vos récompenses", text: `Dès ${threshold} points, convertissez-les en un bon de ${value} € à utiliser chez Palais Maasaï.` },
  ];

  return (
    <div data-testid="fidelite-page">
      <PageHero overline="Fidélité" title="Le programme fidélité" testId="fidelite-hero">
        <p data-testid="fidelite-intro">{siteText(settings, "fidelite") || "Texte à venir."}</p>
      </PageHero>

      <section className="px-6 pb-28 md:px-12 md:pb-40">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-8 md:grid-cols-3">
            {STEPS.map((step, i) => (
              <Reveal key={step.n} delay={i * 0.12}>
                <div
                  className="rounded-lg border border-white/10 bg-coal/60 p-8 transition-colors hover:border-gold/30"
                  data-testid={`fidelite-step-${step.n}`}
                >
                  <span className="text-sm font-bold tracking-[0.3em] text-copper">{step.n}</span>
                  <h2 className="mt-4 flex items-center gap-2 text-xl font-semibold tracking-tight text-ivory">
                    <step.icon className="h-5 w-5 text-gold" strokeWidth={1.5} />
                    {step.title}
                  </h2>
                  <p className="mt-4 text-sm leading-relaxed text-ivory/50">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-16">
            <div className="rounded-lg border border-white/10 bg-coal/40 p-8" data-testid="fidelite-conditions">
              <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">
                Conditions du programme
              </h2>
              <ul className="mt-4 space-y-2 text-sm leading-relaxed text-ivory/55">
                <li>Le programme est gratuit, réservé aux clients majeurs disposant d'un compte.</li>
                <li>
                  Les points sont crédités après chaque règlement : automatiquement pour les achats
                  en ligne, ou par notre équipe en caisse sur présentation de votre carte.
                </li>
                <li>
                  Dès {threshold} points cumulés, un bon de {value} € est généré, utilisable sur un
                  prochain achat éligible, <strong className="text-ivory/80">hors boissons alcoolisées</strong>.
                </li>
                <li>Les bons sont nominatifs, non échangeables, non remboursables et non cumulables entre eux.</li>
                <li>
                  Les points et les bons n'ont pas de valeur monétaire et ne peuvent faire l'objet
                  d'aucun remboursement en espèces.
                </li>
              </ul>
              <h2 className="mt-8 text-sm font-semibold uppercase tracking-[0.2em] text-gold">
                Vos données (RGPD)
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-ivory/55">
                Vos nom, adresse e-mail et historique de points servent uniquement au fonctionnement
                du programme de fidélité. Ils ne sont ni vendus ni partagés avec des tiers. Vous
                pouvez demander à tout moment la consultation, la rectification ou la suppression de
                vos données via la page Contact ou directement auprès de l'établissement.
              </p>
            </div>
          </Reveal>

          <Reveal className="mt-20 text-center">
            <p className="text-sm uppercase tracking-[0.25em] text-ivory/40">
              Suivez vos points et vos récompenses
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link to="/espace" className="btn-primary" data-testid="fidelite-espace-button">
                Accéder à mon espace
              </Link>
              <Link to="/contact" className="btn-outline" data-testid="fidelite-contact-button">
                Contactez-nous
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
